import React, { useState, useEffect, useCallback } from 'react';

/*Styling*/
import { style, classes } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';

/*Misc*/
import { auth } from '../../../../config/firebaseAuth';
import { api } from '../../../../services/api';
import { EPopup } from '@src/types/common';
import { setPopup } from '@src/state/popupAtoms';
import { useQueryUserConfig } from '../../../../queries/users/useQueryUserConfig';

/*Components*/
import { PopupOverlay } from '@src/components/PopupOverlay';
import { Loader } from '@src/components/Loader';
import { GetPasswordLinkButton } from './GetPasswordLinkButton';

/* Types */
import { Services } from '@src/types/Services';

interface Props {
  services: Services;
}

export function UserSettings(props: Props) {
  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;
  const admin = userConfig?.admin;

  const [resetPasswordStatus, setResetPasswordStatus] = useState('none');

  const close = useCallback(() => {
    setPopup(EPopup.userSettings, false);
  }, []);

  const resetPassword = async () => {
    setResetPasswordStatus('sending');
    const c_user = auth.currentUser;
    if (!c_user) {
      setResetPasswordStatus('error');
      return;
    }
    const c_email = c_user.email;
    if (!c_email) {
      setResetPasswordStatus('error');
      return;
    }
    await api.resetPasswordAuthenticated().then(() => {
      setResetPasswordStatus('sent');
    }).catch((err) => {
      console.error(err);
      setResetPasswordStatus('error');
    });
  };

  useEffect(() => {
    setResetPasswordStatus('none');
  }, []);

  if (!user) {
    return null;
  }

  const firebaseUser = auth.currentUser;
  const displayName = firebaseUser?.displayName || '';
  const email = firebaseUser?.email || '';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w[0] ?? '')
    .join('')
    .toUpperCase() || email[0]?.toUpperCase() || '?';

  return (
    <PopupOverlay
      show={true}
      onClose={close}
      title={<span className={titleTextClass}>{window.T('common.settings.title')}</span>}
      width="min(560px, 92vw)"
      cardClassName={cardHeightClass}
      zIndex={3000}
    >
      <div className={contentClass}>

        {/* ── User identity card ── */}
        <div className={identityCardClass}>
          <div className={avatarClass}>{initials}</div>
          <div className={identityInfoClass}>
            {displayName ? <div className={identityNameClass}>{displayName}</div> : null}
            {email ? <div className={identityEmailClass}>{email}</div> : null}
            {admin ? <div className={adminBadgeClass}>Admin</div> : null}
          </div>
        </div>

        {/* ── Security section ── */}
        {!admin ? (
          <section className={sectionClass}>
            <div className={sectionTitleClass}>{window.T('common.settings.password')}</div>

            <div className={passwordRowClass}>
              {resetPasswordStatus === 'none' || resetPasswordStatus === 'error' ? (
                <>
                  <p className={sectionDescClass}>
                    {window.T('common.settings.resetpassword')}
                    <br />
                    <span className={passwordHintClass}>You'll receive an email with a link to create a new password.</span>
                  </p>
                  <GetPasswordLinkButton onAction={() => resetPassword()} />
                </>
              ) : null}

              {resetPasswordStatus === 'sending' ? (
                <div className={statusRowClass}>
                  <Loader size={15} />
                  <span className={sectionDescClass}>Sending…</span>
                </div>
              ) : null}

              {resetPasswordStatus === 'error' ? (
                <div className={errorTextClass}>{window.T('common.settings.resetpassworderror')}</div>
              ) : null}

              {resetPasswordStatus === 'sent' ? (
                <div className={successTextClass}>{window.T('common.settings.resetemailsent')}</div>
              ) : null}
            </div>
          </section>
        ) : null}

      </div>
    </PopupOverlay>
  );
}

const { FLASH_BLUE, FLASH_GREEN, ADALONG_YELLOW } = ADALONG_COLORS;

const titleTextClass = style({
  fontSize: 22,
});

const cardHeightClass = style({
  maxHeight: '85vh',
});

const contentClass = style({
  paddingRight: 8,
});

const identityCardClass = style({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 16,
  backgroundColor: ADALONG_COLORS.DARK_BLACK,
  border: `1px solid rgba(255,255,255,0.08)`,
  borderRadius: 12,
  padding: '16px 20px',
  marginBottom: 24,
});

const avatarClass = style({
  flexShrink: 0,
  width: 52,
  height: 52,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${FLASH_BLUE}, #4466ff)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  fontWeight: 700,
  color: ADALONG_COLORS.LIGHT_WHITE,
  letterSpacing: '0.02em',
});

const identityInfoClass = style({
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minWidth: 0,
});

const identityNameClass = style({
  fontSize: 16,
  fontWeight: 600,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

const identityEmailClass = style({
  fontSize: 13,
  color: ADALONG_COLORS.LIGHT_GRAY,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

const adminBadgeClass = style({
  display: 'inline-block',
  marginTop: 4,
  padding: '2px 8px',
  borderRadius: 99,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  backgroundColor: ADALONG_YELLOW,
  color: ADALONG_COLORS.DARK_BLACK,
});

const sectionClass = style({
  paddingTop: 20,
  borderTop: `1px solid rgba(255,255,255,0.06)`,
  marginBottom: 8,
});

const sectionTitleClass = style({
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: ADALONG_COLORS.SMOOTH_WHITE,
  borderLeft: `3px solid ${FLASH_BLUE}`,
  paddingLeft: 10,
  marginBottom: 14,
});

const sectionDescClass = style({
  fontSize: 13,
  color: ADALONG_COLORS.LIGHT_GRAY,
  lineHeight: '1.5',
  margin: 0,
});

const passwordHintClass = style({
  fontSize: 12,
  color: ADALONG_COLORS.LIGHT_GRAY,
  opacity: 0.7,
});

const passwordRowClass = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  paddingLeft: 13,
});

const statusRowClass = style({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
});

const errorTextClass = style({
  fontSize: 13,
  color: ADALONG_COLORS.FLASH_RED,
});

const successTextClass = style({
  fontSize: 13,
  color: FLASH_GREEN,
});

export default UserSettings;
